











































































































































































































import ModalRecoveryCodeAlert from "@/components/modal/ModalRecoveryCodeAlert.vue";
import { AuthenticationResponseCode, LoadingStatusType, TwoFactorAuthenticationType } from "@/enums/enums";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";

const StoreUserInfo = namespace("UserInfo");

// Interfaces for type safety
interface TwoFactorAuthData {
  type: number;
  referenceOtp?: string;
  twoFactorSecretKey?: string;
  recoveryCodeAttempt?: number;
}

interface OriginalLoginRequest {
  email: string;
  encpw: string;
  userType: number;
  username?: string;
}

interface VerifyOTPParams {
  otp?: string;
  referenceOtp?: string;
  twoFactorSecretKey?: string;
  recoveryCode?: string;
}

@Component({
  components: {
    ModalRecoveryCodeAlert,
  },
})
export default class ModalSignInTwoFactorAuth extends Vue {
  @Prop({ type: Boolean, default: false })
  private showModal!: boolean;

  @StoreUserInfo.Getter
  private getLogInSuccess!: LoadingStatusType;

  @StoreUserInfo.Getter
  private getTwoFactorAuthData!: TwoFactorAuthData | null;

  @StoreUserInfo.Getter
  private getOriginalLoginRequest!: OriginalLoginRequest | null;

  @StoreUserInfo.Getter
  private getTwoFactorErrorMessage!: string;

  @StoreUserInfo.Getter
  private getVerifyNextDelay!: number;

  @StoreUserInfo.Getter
  private getLoginResponseData!: any;

  @StoreUserInfo.Action
  private verifySignInOTP!: (params: VerifyOTPParams) => Promise<void>;

  @StoreUserInfo.Action
  private regenerateSignInOTP!: () => Promise<void>;

  @StoreUserInfo.Action
  private ClearTwoFactorErrorMessage!: () => void;

  @StoreUserInfo.Action
  private SetVerifyNextDelay!: (delay: number) => void;

  private peakFileServer = process.env.VUE_APP_PeakFileServer || "";
  private otpCode = "";
  private otpError = false;
  private resendTimer = 0;
  private resendTimerInterval: number | null = null;
  private verifyDelayTimer = 0;
  private verifyDelayInterval: number | null = null;
  private constants = {
    Timer: {
      RESEND_SECONDS: 60, // 60 วินาที
      RESEND_INTERVAL_MS: 1000, // 1 วินาที
    },
    RecoveryCode: {
      MAX_ATTEMPTS: 10,
    },
  };
  private openIconClose = false;

  // Recovery Code properties
  private showRecoveryCodeStep = false;
  private recoveryCode = "";
  private recoveryCodeError = false;
  private showRecoveryCodeConfirmationStep = false;
  private recoveryCodeAttempts = 0;
  private showRecoveryCodeAlert = false;
  private recoveryCodeAlertType = "warning"; // "warning" or "locked"

  get verifyLoadingStatus(): LoadingStatusType {
    return this.getLogInSuccess;
  }

  get twoFactorAuthData(): TwoFactorAuthData | null {
    return this.getTwoFactorAuthData;
  }

  get originalLoginRequest(): OriginalLoginRequest | null {
    return this.getOriginalLoginRequest;
  }

  get twoFactorErrorMessage(): string {
    return this.getTwoFactorErrorMessage;
  }

  get verifyNextDelay(): number {
    return this.getVerifyNextDelay;
  }

  get TwoFactorAuthenticationType() {
    return TwoFactorAuthenticationType;
  }

  get LoadingStatusType() {
    return LoadingStatusType;
  }

  get loginButtonText(): string {
    if (this.verifyDelayTimer > 0) {
      return this.formatSecondsToMMSS(this.verifyDelayTimer);
    }
    return "เข้าสู่ระบบ";
  }

  get loginButtonIcon(): string {
    if (this.verifyDelayTimer > 0) {
      return "fas fa-circle-notch fa-spin";
    }
    return "";
  }

  get isLoginButtonDisabled(): boolean {
    return (
      this.otpCode.length !== 6 || this.verifyLoadingStatus === LoadingStatusType.Loading || this.verifyDelayTimer > 0
    );
  }

  get titleMessage(): string {
    if (this.twoFactorAuthData?.type === TwoFactorAuthenticationType.Email) {
      return "กรอกรหัส OTP จากอีเมลของคุณ";
    }
    return "กรอกรหัส 6 หลักจากแอปพลิเคชัน Authenticator ของคุณ";
  }

  get isEmailOtp(): boolean {
    return this.twoFactorAuthData?.type === TwoFactorAuthenticationType.Email;
  }

  get isAuthenticatorOtp(): boolean {
    return this.twoFactorAuthData?.type === TwoFactorAuthenticationType.Authenticator;
  }

  get emailUsername(): string {
    return this.originalLoginRequest?.email || "";
  }

  get showReferenceCode(): boolean {
    return this.isEmailOtp && !!this.twoFactorAuthData?.referenceOtp;
  }

  get referenceCode(): string {
    return this.twoFactorAuthData?.referenceOtp || "";
  }

  get isVerifying(): boolean {
    return this.verifyLoadingStatus === LoadingStatusType.Loading;
  }

  get remainingAttempts(): number {
    return this.constants.RecoveryCode.MAX_ATTEMPTS - this.recoveryCodeAttempts;
  }

  get isRecoveryCodeLocked(): boolean {
    // ตรวจสอบจาก loginResponseData เป็นหลัก (ค่าล่าสุดจาก API)
    const currentAttempts = this.loginResponseData?.recovery_code_attempt !== undefined
      ? this.loginResponseData.recovery_code_attempt
      : (this.twoFactorAuthData?.recoveryCodeAttempt || this.recoveryCodeAttempts || 0);
    return currentAttempts >= this.constants.RecoveryCode.MAX_ATTEMPTS;
  }

  get loginResponseData(): any {
    return this.getLoginResponseData;
  }

  @Watch("showModal")
  onShowModalChange(newVal: boolean) {
    if (!newVal) {
      // Reset state when modal is closed
      this.showRecoveryCodeStep = false;
      this.showRecoveryCodeConfirmationStep = false;
      this.recoveryCode = "";
      this.recoveryCodeError = false;
      this.otpCode = "";
      this.otpError = false;
      this.recoveryCodeAttempts = 0;
      this.showRecoveryCodeAlert = false;
      this.recoveryCodeAlertType = "warning";
    }
  }

  @Watch("verifyLoadingStatus")
  onVerifyLoadingStatusChange(newStatus: LoadingStatusType) {
    if (newStatus === LoadingStatusType.None) {
      // ไม่เคลียร์ OTP ทันที - ให้ค้างไว้จนกว่าจะมี error message
      this.otpError = true; // ตั้งค่า otpError = true เมื่อ invalid_otp
      // เคลียร์ verify delay timer เมื่อ loading เสร็จ
      this.clearVerifyDelayTimer();
    } else if (newStatus === LoadingStatusType.Success) {
      // Login สำเร็จ - ปิด modal
      this.ClearTwoFactorErrorMessage(); // เคลียร์ error message
      this.otpError = false; // เคลียร์ otpError
      this.hideModal();
    }
  }

  @Watch("otpCode")
  onOtpCodeChange(newCode: string) {
    if (this.showRecoveryCodeStep) return; // Don't auto-verify when in recovery code step

    if (newCode && this.twoFactorErrorMessage) {
      // เคลียร์ error message เมื่อผู้ใช้เริ่มกรอก OTP ใหม่
      this.ClearTwoFactorErrorMessage();
    }

    if (newCode && newCode.length === 6) {
      this.verifyOTP();
    }
  }

  @Watch("twoFactorErrorMessage")
  onTwoFactorErrorMessageChange(newMessage: string) {
    if (newMessage) {
      // เคลียร์ OTP เมื่อมี error message
      this.otpCode = "";
    }
  }

  @Watch("loginResponseData")
  onLoginResponseDataChange(newData: any, oldData: any) {
    // ตรวจสอบเมื่อ loginResponseData ถูกอัปเดต (สำหรับ recovery code flow)
    if (
      this.showRecoveryCodeStep &&
      !this.showRecoveryCodeConfirmationStep &&
      newData &&
      newData.response_code === AuthenticationResponseCode.DisabledTwoFactor
    ) {
      if (newData.disabled_twofactor === true) {
        // Recovery code ถูกต้อง และ disabled_twofactor = true - แสดง confirmation step
        this.showRecoveryCodeConfirmationStep = true;
        this.recoveryCodeError = false;
        // เคลียร์ error message
        this.ClearTwoFactorErrorMessage();
      } else {
        // Recovery code ผิด - ตรวจสอบว่า recovery_code_attempt เปลี่ยนหรือไม่ เพื่อไม่ให้แสดง modal ซ้ำ
        const oldAttemptCount = oldData?.recovery_code_attempt || this.recoveryCodeAttempts || 0;
        const newAttemptCount = newData.recovery_code_attempt || 0;
        
        // ถ้า attempt count ไม่เปลี่ยน ไม่ต้องทำอะไร (ป้องกันการแสดง modal ซ้ำ)
        if (oldAttemptCount === newAttemptCount && oldData) {
          return;
        }

        // อัปเดต attempt count จาก API response
        this.recoveryCodeAttempts = newAttemptCount;
        this.recoveryCodeError = true;
        this.recoveryCode = "";

        // ตรวจสอบจำนวน attempt และแสดง modal ที่เหมาะสม (แสดงที่ครั้ง 3, 6, 10 เท่านั้น)
        if (newAttemptCount === this.constants.RecoveryCode.MAX_ATTEMPTS) {
          // ถึง 10 ครั้งแล้ว - แสดง locked modal แล้วปิดทุกอย่าง
          this.recoveryCodeAlertType = "locked";
          this.showRecoveryCodeAlert = true;
        } else if (newAttemptCount === 3 || newAttemptCount === 6) {
          // แสดง warning modal ที่ครั้งที่ 3 และ 6 เท่านั้น
          this.recoveryCodeAlertType = "warning";
          this.showRecoveryCodeAlert = true;
        } else {
          // ปิด modal เมื่อ attempt ไม่ใช่ 3, 6, 10
          this.showRecoveryCodeAlert = false;
        }
      }
    }
  }

  async verifyOTP() {
    // ตรวจสอบว่า OTP มี 6 หลักหรือไม่
    if (this.otpCode.length !== 6) {
      this.otpError = true;
      return;
    }

    // เริ่มนับเวลา VerifyNextDelay ทันทีถ้ามี (ครั้งที่ 2 เป็นต้นไป)
    if (this.verifyNextDelay > 0) {
      this.startVerifyDelayTimer(this.verifyNextDelay);
    }

    try {
      // ส่งข้อมูลตามประเภท 2FA
      if (this.twoFactorAuthData?.type === TwoFactorAuthenticationType.Email) {
        // Email OTP - ใช้ referenceOtp
        await this.verifySignInOTP({
          otp: this.otpCode,
          referenceOtp: this.twoFactorAuthData.referenceOtp,
        });
      } else if (this.twoFactorAuthData?.type === TwoFactorAuthenticationType.Authenticator) {
        // Authenticator OTP - ใช้ twoFactorSecretKey
        await this.verifySignInOTP({
          otp: this.otpCode,
          twoFactorSecretKey: this.twoFactorAuthData.twoFactorSecretKey,
        });
      }

      // ไม่ reset form ทันที - ให้ OTP ค้างไว้จนกว่าจะมี error message
      this.otpError = false;
    } catch (error) {
      console.error("Verify OTP Error:", error);
      this.otpError = true;
    }
  }

  hideModal() {
    this.$emit("update:showModal", false);
  }

  startResendTimer() {
    this.resendTimer = this.constants.Timer.RESEND_SECONDS; // เริ่มต้นที่ 60 วินาที
    this.resendTimerInterval = setInterval(() => {
      if (this.resendTimer > 0) {
        this.resendTimer--;
      } else {
        if (this.resendTimerInterval) {
          clearInterval(this.resendTimerInterval);
        }
        this.resendTimerInterval = null;
      }
    }, this.constants.Timer.RESEND_INTERVAL_MS); // 1 วินาที = 1000 มิลลิวินาที
  }

  startVerifyDelayTimer(delayMs: number) {
    // เคลียร์ timer เก่าก่อน
    if (this.verifyDelayInterval) {
      clearInterval(this.verifyDelayInterval);
    }

    // แปลง ms เป็นวินาที
    this.verifyDelayTimer = Math.ceil(delayMs / 1000);
    this.verifyDelayInterval = setInterval(() => {
      if (this.verifyDelayTimer > 0) {
        this.verifyDelayTimer -= 1; // ลดลงทีละ 1 วินาที
      } else {
        if (this.verifyDelayInterval) {
          clearInterval(this.verifyDelayInterval);
        }
        this.verifyDelayInterval = null;
        this.verifyDelayTimer = 0;
      }
    }, 1000); // อัปเดตทุก 1 วินาที
  }

  formatSecondsToMMSS(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  }

  clearVerifyDelayTimer() {
    if (this.verifyDelayInterval) {
      clearInterval(this.verifyDelayInterval);
      this.verifyDelayInterval = null;
    }
    this.verifyDelayTimer = 0;
  }

  async resendOtp() {
    // Resend OTP เฉพาะ Email type เท่านั้น
    if (this.twoFactorAuthData?.type !== TwoFactorAuthenticationType.Email) return;
    if (this.resendTimer > 0) return; // ไม่ให้กดถ้ายังไม่หมดเวลา

    try {
      // เคลียร์ error message และ reset สถานะ
      this.ClearTwoFactorErrorMessage();
      this.otpError = false;
      this.otpCode = ""; // เคลียร์ OTP input ด้วย

      await this.regenerateSignInOTP();
      this.startResendTimer(); // เริ่มนับถอยหลังใหม่
    } catch (error) {
      console.error("Resend OTP Error:", error);
    }
  }

  mounted() {
    // เริ่มนับถอยหลังเมื่อ modal เปิด (เฉพาะ Email type)
    if (this.twoFactorAuthData?.type === TwoFactorAuthenticationType.Email) {
      this.startResendTimer();
    }
    // เคลียร์ error message เมื่อ modal เปิด
    this.ClearTwoFactorErrorMessage();
  }

  // Recovery Code Methods
  goToRecoveryCodeStep() {
    if (this.isRecoveryCodeLocked) {
      return;
    }
    this.showRecoveryCodeStep = true;
    this.recoveryCode = "";
    this.recoveryCodeError = false;
    // Reset alert modal state เมื่อเข้า recovery code step
    this.showRecoveryCodeAlert = false;
  }

  goBackToOTPStep() {
    this.showRecoveryCodeStep = false;
    this.recoveryCode = "";
    this.recoveryCodeError = false;
  }

  onRecoveryCodeInput() {
    this.recoveryCodeError = false;
    this.ClearTwoFactorErrorMessage();
  }

  async verifyRecoveryCode() {
    if (!this.recoveryCode || this.recoveryCode.trim() === "") {
      this.recoveryCodeError = true;
      return;
    }

    if (this.verifyNextDelay > 0) {
      this.startVerifyDelayTimer(this.verifyNextDelay);
    }

    try {
      await this.verifySignInOTP({
        recoveryCode: this.recoveryCode.trim(),
      });

      await this.$nextTick();

      // ตรวจสอบ loading status และ error message หลังจาก dispatch
      // ตรวจสอบ response_code 305 และ disabled_twofactor: true
      if (
        this.verifyLoadingStatus === LoadingStatusType.None &&
        this.loginResponseData?.response_code === AuthenticationResponseCode.DisabledTwoFactor &&
        this.loginResponseData?.disabled_twofactor === true
      ) {
        // Recovery code ถูกต้อง และ disabled_twofactor = true - แสดง confirmation step
        this.showRecoveryCodeConfirmationStep = true;
        this.recoveryCodeError = false;
        this.ClearTwoFactorErrorMessage();
      } else if (this.verifyLoadingStatus === LoadingStatusType.Success) {
        // Login สำเร็จ (กรณี disabled_twofactor = false)
        this.recoveryCode = "";
        this.recoveryCodeError = false;
        this.hideModal();
      } else if (
        this.verifyLoadingStatus === LoadingStatusType.Fail ||
        this.twoFactorErrorMessage ||
        (this.loginResponseData?.response_code === AuthenticationResponseCode.DisabledTwoFactor &&
          this.loginResponseData?.disabled_twofactor === false)
      ) {
        // Recovery code ผิด - อัปเดต attempt count จาก API response
        // การแสดง modal จะถูกจัดการโดย watch loginResponseData เท่านั้น
        const attemptCount = this.loginResponseData?.recovery_code_attempt || 0;
        this.recoveryCodeAttempts = attemptCount;
        this.recoveryCodeError = true;
        this.recoveryCode = "";
      }
    } catch (error) {
      console.error("Verify Recovery Code Error:", error);
      this.recoveryCodeError = true;
      this.recoveryCode = "";
    }
  }

  handleRecoveryCodeAlertClose() {
    if (this.recoveryCodeAlertType === "locked") {
      this.hideModal();
    }
  }

  async confirmLoginWithout2FA() {
    if (this.verifyNextDelay > 0) {
      this.startVerifyDelayTimer(this.verifyNextDelay);
    }

    try {
      await this.verifySignInOTP({
        otp: "",
      });

      if (this.verifyLoadingStatus === LoadingStatusType.Success) {
        this.hideModal();
      }
    } catch (error) {
      console.error("Confirm Login Error:", error);
    }
  }

  beforeDestroy() {
    // เคลียร์ timer เมื่อ component ถูกทำลาย
    if (this.resendTimerInterval) {
      clearInterval(this.resendTimerInterval);
    }
    if (this.verifyDelayInterval) {
      clearInterval(this.verifyDelayInterval);
    }
  }
}
